import { HttpTransportType, HubConnectionState, IHttpConnectionOptions } from '@microsoft/signalr';

import { JackpotUpdateModel, JackpotWinModel, JackpotWinnersModel, JackpotWinPopupModel } from '../../../models';
import { loadingStore } from '../../../jackpot/modules/frameModule/blocks/loading';
import { failLaunch } from '../../../services/launch-telemetry';
import { BaseLiveConnection } from '../../../services';
import { env } from '../../../environment';
import { RootStore } from '../../../store';
import { guid } from '../../../helpers';

class JackpotLiveConnection extends BaseLiveConnection {

  private rootStore?: RootStore;

  public async startSocketLifecycle(partnerId: number, gameId: number, currencyCode: string, rootStore: RootStore): Promise<void> {
    await this.start(partnerId, gameId, currencyCode, rootStore);
    this.setupReconnectionLifecycle();
  }

  private async start(partnerId: number, gameId: number, currencyCode: string, rootStore: RootStore): Promise<void> {
    this.rootStore = rootStore;
    const { authInfo } = rootStore.authStore;
    const socketOptions: IHttpConnectionOptions = {
      accessTokenFactory: () => authInfo?.accessToken || '',
      skipNegotiation: true,
      transport: HttpTransportType.WebSockets,
    };

    await this.rootStore.jackpotStore.loadJackpotResources();

    loadingStore.setJackpotConnectionOpen();

    await loadingStore.setEndLoadingProcess();

    await this.init(socketOptions, { affinityId: guid() });
    rootStore.jackpotStore.listenEvents();

    await this.invokeConnect(partnerId, gameId, currencyCode);

    this.onReconnected(() => {
      this.invokeConnect(partnerId, gameId, currencyCode);
    });

    this.invokeJackpotWinners(partnerId, gameId, currencyCode);
  }
  private setupReconnectionLifecycle(): void {
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
  }

  protected handleInvokeError(methodName: string, error: Error): void {
    // 401 on a hub invoke means OUR token, not the operator wallet. No popup.
    failLaunch('jackpot_invoke_error', `${methodName}: ${error?.message ?? ''}`.slice(0, 300));
  }

  private handleVisibilityChange = (): void => {
    if (!document.hidden) {
      if (this.connection?.state === HubConnectionState.Disconnected) {
        const data = this.rootStore?.gameStore.launchData?.data;
        if (data && this.rootStore) {
          const { partnerId, gameId, currency } = data;
          this.start(partnerId, gameId, currency, this.rootStore);
        }
      }
    }
  }

  public async invokeConnect(partnerId: number, gameId: number, currencyCode: string): Promise<void> {
    await this.invoke('Connect', partnerId, gameId, currencyCode);
  }

  public invokeJackpotWinners(partnerId: number, gameId: number, currencyCode: string): void {
    this.invoke('JackpotWinners', partnerId, gameId, currencyCode);
  }

  public invokeOpenJackpotSceneItem(index: number): void {
    this.invoke('OpenJackpotSceneItem', { index });
  }

  public invokeOpenJackpotRemainingScene(): void {
    this.invoke('OpenJackpotScene');
  }

  public invokeJackpotWinSeen(): void {
    this.invoke('PlayerSeen');
  }

  public onOpenJackpotScene(handler: (data: any) => void): void {
    this.on('OpenJackpotScene', handler);
  }

  public onJackpotUpdate(handler: (data: JackpotUpdateModel) => void): void {
    this.on('JackpotUpdate', handler);
  }

  public onOpenJackpotSceneInitial(handler: (data: any) => void): void {
    this.on('OpenJackpotSceneInitial', handler);
  }

  public onOpenJackpotSceneResult(handler: (data: any) => void): void {
    this.on('JackpotSceneResult', handler);
  }

  public onJackpotWin(handler: (data: JackpotWinModel) => void): void {
    this.on('JackpotWin', handler);
  }

  public onJackpotSceneResult(handler: any): void {
    this.on('JackpotSceneResult', handler);
  }

  public onOpenJackpotSceneItemResut(handler: any): void {
    this.on('JackpotSceneItemResult', handler);
  }

  public onJackpotWinnerResult(handler: (data: Array<JackpotWinnersModel>) => void): void {
    this.on('JackpotWinnerResult', handler);
  }

  public onLastJackpotWin(handler: (data: JackpotWinPopupModel) => void): void {
    this.on('LastJackpotWin', handler);
  }

}

export const jackpotSocket = new JackpotLiveConnection(env.jackpotWSUrl);
